import {
  useState,
  useContext,
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
} from 'react';
import { Form, Formik, FormikProps, FormikTouched } from 'formik';
import { FaUser } from 'react-icons/fa';
import {
  IoIosAddCircle,
  IoMdBriefcase,
  IoMdBusiness,
  IoMdPin,
  IoIosPeople,
} from 'react-icons/io';
import { VscHome } from 'react-icons/vsc';
import { Divider } from '@mui/material';
import { toast } from 'react-toastify';

import * as CXT from 'context/globalContext';
import Button from 'components/Button';
import MaskedInput from 'components/MaskedInput';
import { cpfMask, unMask } from 'utils/mask';
import { findData } from 'services/findCEP';
import { Person } from 'services/client';
import { Register } from 'services/register';
import { MaritalStatus } from 'services/maritalStatus';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import convertDataToSubmit from '../../Register/utils/convertDataToSubmit';
import { ContainerWithInformation } from '../../Register/components/ComponentWithInformation';
import { InputsAndSelectsOfBasicInformation } from '../../Register/components/InputsAndSelectsOfBasicInformation';
import { InputsForInformationAddress } from '../../Register/components/InputsForInformationAddress';
import { InputsAndSelectsOfProfissionalInformation } from '../../Register/components/InputsAndSelectsOfProfissionalInformation';
import { ButtonsForm } from '../../Register/components/ButtonsForm';
import { FormPropsPJType } from '../../Register/utils/types/FormPropType/FormPropPJType';
import {
  returnsWithCapitalLettersAndWithoutAccent,
  removeWhiteSpace,
} from '../../Register/utils/formFunctions/returnsWithCapitalLettersAndWithoutAccent';
import { initialData } from '../../Register/utils/initialData/intialValuesForPJForm';
import { findEnderecoIndex } from '../../Register/utils/convertDataToSubmit';

import * as S from './styles';
import {
  EstadoCivilProps,
  ProfissaoProps,
  TipoOcupacaoProps,
} from 'pages/Register/utils/personProps';
import { find, size, some } from 'lodash';
import { validation } from 'pages/Register/utils/validation/validation';
import { validationPJ } from 'pages/Register/utils/validation/validationPJ';
import { InputsAndSelectsOfBasicPJInformation } from 'pages/Register/components/InputsAndSelectsOfBasicPJInformation';
import {
  FormPropsType,
  FormPropsWithNoAvalistaType,
} from 'pages/Register/utils/types/FormPropType/FormPropType';
import convertPJDataToSubmit from 'pages/Register/utils/convertPJDataToSubmit';
import { InputsAndSelectsOfCompanyData } from 'pages/Register/components/InputsAndSelectsOfCompanyData';
import { InputsAndSelectsOfPartnersData } from 'pages/Register/components/InputsAndSelectsOfPartnersData';

Bugsnag.start({
  apiKey: '00621c4c68a1b2bd34e7458c0f8d29bc',
  plugins: [new BugsnagPluginReact()],
});

type PessoaJuridicaFormProps = {
  customerCnpj: string;
  municipios: any[];
  submitButtonLabel?: string;
  stopLoadingOnFinish?: boolean;
  finishCallback: (registerResponse: any) => void;
  missingCustomerDataCallback?: () => void;
};

const PessoaJuridicaForm = forwardRef(function PessoaJuridicaForm(
  {
    customerCnpj,
    municipios,
    stopLoadingOnFinish = true,
    submitButtonLabel,
    finishCallback,
    missingCustomerDataCallback,
  }: PessoaJuridicaFormProps,
  ref
) {
  const globalContext = useContext(CXT.Context);
  const [customer, setCustomer] = useState<any>({});

  const [comprovantes, setComprovantes] = useState<ProfissaoProps[]>([]);
  const [profissoes, setProfissoes] = useState<ProfissaoProps[]>([]);
  const [estadosCivis, setEstadosCivis] = useState<EstadoCivilProps[]>([]);
  const [ocupacoes, setOcupationList] = useState<TipoOcupacaoProps[]>([]);
  // const [tipoOcupacao, setTipoOcupacao] = useState(0);
  // const [ocupacoesTags, setOcupacoesTags] = useState('');
  const [successForm, setSuccessForm] = useState({} as FormPropsPJType);
  const [errorsForm, setErrorsForm] = useState({} as FormPropsPJType);
  const [formSubmited, setFormSubmited] = useState(false);

  const [initialValues, setInitialValues] = useState<FormPropsPJType>(
    initialData(undefined, -1, undefined, () => '')
  );

  const [nacionalidades, setNacionalidades] = useState<
    { id: number; descricao: string }[]
  >([]);

  const updateSuccessForm = (data: any) => setSuccessForm({ ...data });
  const updateErrorsForm = (data: any) => setErrorsForm({ ...data });

  const formikRef = useRef<FormikProps<any>>(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      formikRef.current?.submitForm();
    },
  }));

  useEffect(() => {
    getOptions();
    loadPersonData();
  }, [customerCnpj]);

  useEffect(() => {
    findData
      .GetNacionalidades()
      .then((response) => {
        setNacionalidades(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar as nacionalidades');
      });
  }, []);

  async function getOptions() {
    MaritalStatus.Get()
      .then((response) => {
        setEstadosCivis(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar os estados civis');
      });

    findData
      .GetTipoOcupação()
      .then((response) => {
        setOcupationList(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        setOcupationList([
          {
            codigo: '1',
            descricao: 'Funcionário de Iniciativa Privada',
            abreviacao: '1',
            tagValidacao:
              '#renda##rp_nome_empresa##rp_municipio##rp_telefone##rp_admissao##profissao#',
          },
          {
            codigo: '10',
            descricao: 'Estudante',
            abreviacao: '10',
            tagValidacao: '#renda#',
          },
        ]);
      });

    Person.JobsRequest()
      .then((response: any) => {
        setProfissoes(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar as profissões');
      });

    Person.ComprovantRequest()
      .then((response: any) => {
        setComprovantes(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar os tipos de comprovantes');
      });
  }

  const loadPersonData = () => {
    globalContext.setIsLoading(true);
    const unmaskedCnpj = unMask(customerCnpj, '[^0-9A-Za-z]').toUpperCase();

    Person.ClientPJRequest(unmaskedCnpj)
      .then(async (response) => {
        const foundPerson = response?.data?.data;

        setCustomer(foundPerson);
        localStorage.setItem('client', JSON.stringify(foundPerson));

        let phone: any;

        try {
          if (foundPerson.telefones) {
            phone = foundPerson.telefones.find((item: any) => {
              return item.status === 1 && item.preferencia === true;
            });
          }
        } catch {
          if (missingCustomerDataCallback) {
            missingCustomerDataCallback();
          }
        }

        const getEmail = () => {
          let aux = '';

          foundPerson?.emails?.forEach(
            (value: {
              email: string;
              preferencia: boolean;
              status: number;
            }) => {
              if (value.preferencia === true && value.status === 1) {
                aux = value.email;
              }
            }
          );

          return aux;
        };

        const enderecoIndex = findEnderecoIndex(foundPerson?.enderecos);

        const formData = initialData(
          foundPerson,
          enderecoIndex,
          phone,
          getEmail
        ) as FormPropsPJType;
        setInitialValues(formData);
      })
      .catch((error) => {
        globalContext.setIsLoading(false);
        if (error.response?.status === 404) {
          return toast.error(
            'Cliente não encontrado com esse CPF. Verifique os dados digitados e tente novamente'
          );
        }

        if (error.response?.status === 500) {
          return toast.error(
            'Ops! Tivemos um problema ao prosseguir com a sua solicitação. ' +
              'Tente novamente mais tarde e se o problema persistir contate o suporte!'
          );
        }

        return toast.error(
          'Não foi possível prosseguir com a solicitação. Faça o Login novamente.'
        );
      })
      .finally(() => {
        globalContext.setIsLoading(false);
      });
  };

  const normalize = (value: string) => {
    return removeWhiteSpace(returnsWithCapitalLettersAndWithoutAccent(value));
  };

  const findMunicipio = (cidade: string, uf: string) => {
    return find(
      municipios,
      (it) =>
        normalize(it.descricao) === normalize(cidade) &&
        normalize(it.codigoEstado) === normalize(uf)
    );
  };

  const findItem = (
    list: EstadoCivilProps[] | ProfissaoProps[],
    value: string
  ): EstadoCivilProps | ProfissaoProps => {
    const found = find(list, (posicao) => posicao.descricao === value);
    if (!found) throw new Error('Item not found');
    return found;
  };

  const submitCustomer = (person: any, formData: FormPropsPJType) => {
    return new Promise((resolve, error) => {
      const cliente = findMunicipio(formData.cidade, formData.uf);
      // const empresa = findMunicipio(formData.cidadeEmpresa, formData.ufEmpresa);
      // const municipioEmpresaId = empresa?.id;
      const dataMunicipio = {
        id: cliente.id,
        descricao: returnsWithCapitalLettersAndWithoutAccent(cliente.descricao),
        codigoEstado: normalize(cliente.codigoEstado),
      };
      // const estadoCivil = findItem(estadosCivis, formData.estadoCivil);
      // const profissao = findItem(profissoes, formData.profissao);
      // const comprovacao = findItem(comprovantes, formData.comprovacao);
      const updatedJson = convertPJDataToSubmit(
        person,
        formData,
        dataMunicipio
      );
      const postData = {
        messages: null,
        data: updatedJson,
      };
      Register.PostCompany(postData)
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((err) => {
          Bugsnag.notify(err);
          error();
        });
    });
  };

  const validateCidadeUf = (formData: FormPropsPJType) => {
    const municipioCliente = findMunicipio(formData.cidade, formData.uf);
    if (!municipioCliente) {
      formData.cidade = '';
      formData.uf = '';
      toast.error('Endereço Pessoal: Cidade ou UF inexistente');
    }
  };

  const isFormValid = (formData: FormPropsPJType) => {
    validateCidadeUf(formData);
    const resultado = validationPJ(formData);
    setSuccessForm(resultado[0]);
    setErrorsForm(resultado[1]);

    if (resultado[2]) {
      globalContext.setIsLoading(true);
      return true;
    }
    return false;
  };

  const handleSubmit = (formData: FormPropsPJType) => {
    if (!isFormValid(formData)) {
      return;
    }

    setFormSubmited(true);
    submitCustomer(customer, formData)
      .then((response) => {
        if (stopLoadingOnFinish) {
          toast.success('Salvo com sucesso!');
        }
        finishCallback(response);
      })
      .catch(() => {
        toast.error('Erro ao salvar o cliente');
      })
      .finally(() => {
        if (stopLoadingOnFinish) {
          globalContext.setIsLoading(false);
        }
      });
  };

  const validOnChangeAfterError =
    (
      updateSuccessForm: (data: any) => void,
      updateErrorsForm: (data: any) => void,
      ocupacaoValidationTags = ''
    ) =>
    (
      campSelect: string,
      eventTargetValue: string,
      values: FormPropsPJType | FormPropsWithNoAvalistaType | FormPropsType
    ) => {
      if (formSubmited && eventTargetValue !== undefined) {
        const array = Object.entries(values);
        const arrayWithIndexOneOfArray = array.map((posicao) => posicao[0]);

        if (campSelect === 'mesAnoAdmissao') {
          eventTargetValue = eventTargetValue.replace('/', '');
        }

        const indexForChange = arrayWithIndexOneOfArray.indexOf(campSelect);
        array[indexForChange][1] = eventTargetValue;

        const changeValueInArray = Object.fromEntries(array);

        if ((values as FormPropsPJType).cnpj !== undefined) {
          const newObjectWithValues = changeValueInArray as FormPropsPJType;
          const valid = validationPJ(newObjectWithValues);
          updateSuccessForm(valid[0]);
          updateErrorsForm(valid[1]);
        } else {
          const newObjectWithValues =
            changeValueInArray as FormPropsWithNoAvalistaType;
          const valid = validation(
            newObjectWithValues,
            estadosCivis,
            ocupacoes.map((posicao) => Number(posicao.codigo)),
            comprovantes,
            ocupacaoValidationTags,
            profissoes
          );
          updateSuccessForm(valid[0]);
          updateErrorsForm(valid[1]);
        }
      }
    };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={(values: FormPropsPJType) => handleSubmit(values)}
      enableReinitialize={true}
    >
      {({ touched, values, setFieldValue }: FormikProps<FormPropsPJType>) => (
        <Form>
          <S.Content>
            <S.Left>
              <S.BasicInfo>
                <ContainerWithInformation
                  Icon={IoMdBusiness}
                  text={'Informações Básicas do Cliente'}
                  identifier={values.cnpj}
                  identifierType={'cnpj'}
                />
                <InputsAndSelectsOfBasicPJInformation
                  isModifying
                  values={values}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </S.BasicInfo>
              <S.AddressContact>
                <ContainerWithInformation
                  Icon={IoMdPin}
                  text={'Endereço e contato'}
                />

                <InputsForInformationAddress
                  values={values}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  validOnChangeAfterError={validOnChangeAfterError(
                    updateSuccessForm,
                    updateErrorsForm,
                    ''
                  )}
                  formSubmited={formSubmited}
                />
              </S.AddressContact>
            </S.Left>
            <S.Right>
              <S.ProfessionalInfo>
                <ContainerWithInformation
                  Icon={IoIosAddCircle}
                  text={'Informações Adicionais'}
                />
                <InputsAndSelectsOfCompanyData
                  values={values}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </S.ProfessionalInfo>
              <S.ProfessionalInfo>
                <ContainerWithInformation Icon={IoIosPeople} text={'Sócios'} />
                <InputsAndSelectsOfPartnersData
                  values={values}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </S.ProfessionalInfo>
            </S.Right>
          </S.Content>
          <S.HContainer>
            {globalContext.isLoading ? (
              ''
            ) : (
              <ButtonsForm
                isModifying
                hideWarning
                submitButtonLabel={submitButtonLabel}
              />
            )}
          </S.HContainer>
        </Form>
      )}
    </Formik>
  );
});

export default PessoaJuridicaForm;
